import {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import {ApiError, callSmsCode, completeOAuthSignup, completeSignup, createNativeHandoff, fetchPendingOAuth, linkPendingOAuth, pollLogin, requestEmailCode, resendLoginCode, verifyEmailCode, type PendingOAuthProfile, type RequestResult, requestSmsCode, verifySmsCode} from "./api";
import {getReturnTo, readTokens, saveTokens} from "./storage";
import {dismissPasskeySetup, enrollPasskey, shouldOfferPasskeySetup, signInWithPasskey, userIdFromAccessToken, type PasskeySignInResult} from "./passkey";
import {browserSupportsWebAuthn} from "@simplewebauthn/browser";
import {buildNativeAppCallbackUrl, buildNativeAppIntentUrl, buildNativeExternalLoginReturnTo, buildNativeProviderLoginUrl, getNativeParentOrigin, isNativeAppLoginReturnTo, isNativeProviderLoginReturnTo, NATIVE_APP_CALLBACK_URL} from "../../shared/login-return-to.ts";
import {appendReferralParams, captureReferralAttributionFromUrl, clearStoredReferralAttribution} from "./referral";
import {enabledProviders, providerLabel} from "./auth-config";
import {activeDeployment, logoBox, phoneCountryAdmitted} from "../../shared/deployment-config.ts";
import {parseAgeYears} from "../../shared/date-of-birth.ts";
import {loginCompletionMayCarryTokens} from "../../shared/login-completion.ts";
import {signupInputIsValid, signupInputProblem, SIGNUP_NAME_MAX_LENGTH, type SignupInput, type SignupProfileError} from "../../shared/signup-profile.ts";
import {getUserNameValidationError, isEmailAddress, normaliseSignupUserNameInput, userNameSuggestionFromEmail, toUserNameSuggestion, USER_NAME_MAX_LENGTH, type UserNameValidationError} from "../../shared/user-name.ts";
import {PhoneVerifyPage} from "./PhoneVerifyPage";
import {useHumanCheck} from "./humanCheck";
import {ContactField, contactE164, initialContact, type ContactAccept, type ContactValue} from "@twoshot/ui-kit/phone/react";
import {countryByCode, formatE164, isEmail} from "@twoshot/ui-kit/phone";
import {flagUrl} from "./flags";
import {BackIcon, CheckIcon, CloseIcon, ExternalIcon, MobileIcon, PasskeyIcon} from "@twoshot/ui-kit/ui";

// State machine for the login flow:
//   email-entry  → user types email
//   code-entry   → existing users enter code; new users enter code + signup details
//   signup       → fallback for magic-link/poll paths where the code was already verified
//   success      → tokens stored, redirecting to return_to

type Step = "email-entry" | "phone-entry" | "code-entry" | "signup" | "success" | "magic-landed" | "passkey-offer" | "provider-signup" | "provider-error";

// The page's one brand mark. A wordmark (TwoShot's) spells the product's name, so it heads the
// page and the heading drops "to TwoShot": the name shows once. A symbol mark (Forge's) is never
// drawn here: the orb in the magma behind the form already is the mark, and the heading names
// the product (owner, 10 Oct: no logo on Forge's sign-in; TwoShot keeps its new logo).
function BrandMark() {
    const cfg = activeDeployment();
    const logo = cfg.logo;
    if (!logo.wordmark) return null;
    const {width, height} = logoBox(logo);
    return (
        <div className="logo">
            {logo.kind === "image"
                ? <img src={logo.url} alt={cfg.appName} style={{width: width ?? "auto", height, objectFit: "contain"}}/>
                : <svg viewBox={logo.viewBox} width={width ?? undefined} height={height} xmlns="http://www.w3.org/2000/svg"
                       role="img" aria-label={cfg.appName}>
                    <path d={logo.path}/>
                </svg>}
        </div>
    );
}

/** "Sign in to Forge", or null under a wordmark: the wordmark already is the page's one identity
 *  line, and the buttons say the rest (UI commandment 16: say it once). */
function signInHeading(): string | null {
    const cfg = activeDeployment();
    return cfg.logo.wordmark ? null : `Sign in to ${cfg.appName}`;
}

/** A screen's heading, with the back button (when there is one) in reserved space on its left.
 *  With no visible title the heading stays for screen readers only. */
function Head({title, onBack}: {title: React.ReactNode | null; onBack?: () => void}) {
    const hidden = title === null;
    if (hidden && !onBack) return <h1 className="sr-only">{`Sign in to ${activeDeployment().appName}`}</h1>;
    return (
        <div className={hidden ? "head untitled" : "head"}>
            {onBack && (
                <button type="button" className="btn btn-ghost btn-icon btn-md" onClick={onBack} aria-label="Back"><BackIcon/></button>
            )}
            <h1 className={hidden ? "sr-only" : undefined}>{hidden ? `Sign in to ${activeDeployment().appName}` : title}</h1>
        </div>
    );
}

// Generic centered result panel — used for success ("You're in") AND error
// ("Sign-in failed") states with the same layout, just an icon/tint swap.
// Keeping one component means the two states stay visually consistent
// (same spacing, same heading rhythm, same action layout).
function ResultPanel({variant = "success", icon, title, message, actions}: {
    variant?: "success" | "error";
    icon: React.ReactNode;
    title: string;
    message?: React.ReactNode;
    actions?: React.ReactNode;
}) {
    return (
        <div className={`result-panel ${variant === "error" ? "result-error" : ""}`}>
            <div className="icon">{icon}</div>
            <h2>{title}</h2>
            {message && <p className="subtitle">{message}</p>}
            {actions && <div className="actions">{actions}</div>}
        </div>
    );
}

// Map provider-callback error codes (from server-side redirectError) to a
// user-facing message. Unknowns fall back to the generic line.
// Apple private-relay needs the recovery instructions because Apple sticks
// to the user's first choice forever — without revoking they're stuck.
const APPLE_REVOKE_URL = "https://account.apple.com/account/manage/section/security";
// Built at RENDER time, never at module scope: this record interpolates the
// active deployment's name, and App.tsx's module body runs BEFORE main.tsx
// calls adoptInjectedDeployment(), so a module-scope literal would capture
// the compiled-in TwoShot default permanently.
function providerErrorCopyTable(): Record<string, React.ReactNode> {
    return {
        email_not_verified: "Your account's email isn't verified at the provider. Verify with them then try again.",
        email_not_allowed: "This email address can't be used to sign in. Try a different one.",
        invalid_or_expired_state: "Your sign-in session expired. Please try again.",
        missing_code_or_state: "Sign-in wasn't completed. Please try again.",
        provider_not_configured: "This sign-in method isn't available right now.",
        instagram_professional_required: "Instagram sign-in currently supports Business and Creator accounts. Use email sign-in and add a legacy Instagram handle from Settings for a personal account.",
        apple_token_exchange_failed: "Apple couldn't verify your sign-in. Please try again.",
        apple_private_relay_unsupported: (
            <>
                Apple's <b>"Hide My Email"</b> isn't supported. Choose <b>"Share My Email"</b> instead so we can reach you about your account.
                <br/><br/>
                Apple remembers your first choice — to fix it: <a href={APPLE_REVOKE_URL} target="_blank" rel="noopener noreferrer">open your Apple ID security settings</a>, find {activeDeployment().appName} under <i>Sign in with Apple</i>, click <b>Stop using</b>, then try signing in again.
            </>
        ),
    };
}
const NATIVE_PLATFORM_KEY = "native_platform";
const AUTH_EXTERNAL_OPEN_MESSAGE_TYPE = "twoshot:open-external-auth";

function providerErrorCopy(code: string | null): React.ReactNode {
    if (!code) return "Something went wrong signing you in. Please try again.";
    return providerErrorCopyTable()[code] ?? "Something went wrong signing you in. Please try again.";
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

function detailNumber(detail: unknown, key: string): number | null {
    if (!isRecord(detail)) return null;
    const value = detail[key];
    return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function hasErrorName(error: unknown, name: string): boolean {
    return isRecord(error) && error.name === name;
}

export function App() {
    if (isNativeCallbackPath()) return <NativeAppCallback/>;
    if (isPhonePagePath()) return <PhoneVerifyPage/>;
    return <LoginApp/>;
}

function NativeAppCallback() {
    const [nativeUrl, setNativeUrl] = useState<string | null>(null);
    const [hasTokens, setHasTokens] = useState(true);

    useEffect(() => {
        const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ""));
        const accessToken = fragment.get("access_token");
        const refreshToken = fragment.get("refresh_token");
        const appReturnTo = new URLSearchParams(window.location.search).get("app_return_to") || "/";
        const handoffTicket = new URLSearchParams(window.location.search).get("handoff_ticket");
        const callbackUrl = handoffTicket
            ? buildNativeAppCallbackUrl(appReturnTo, handoffTicket)
            : new URL(NATIVE_APP_CALLBACK_URL);
        // Null when this deployment has no native app: nothing to hand off to, so
        // show the failure state rather than navigating somewhere wrong.
        if (callbackUrl === null) { setHasTokens(false); return; }
        if (!handoffTicket) callbackUrl.searchParams.set("app_return_to", appReturnTo);
        if (accessToken && refreshToken) {
            callbackUrl.hash = new URLSearchParams({access_token: accessToken, refresh_token: refreshToken}).toString();
        } else if (!handoffTicket) {
            setHasTokens(false);
        }

        const nextUrl = callbackUrl.toString();
        const launchUrl = (isAndroid() ? buildNativeAppIntentUrl(callbackUrl) : null) ?? nextUrl;
        setNativeUrl(launchUrl);

        const cleanUrl = new URL(window.location.href);
        cleanUrl.hash = "";
        window.history.replaceState(null, "", cleanUrl.toString());

        window.location.replace(launchUrl);
    }, []);

    return (
        <div className="auth-container">
            <BrandMark/>
            <ResultPanel
                variant={hasTokens ? "success" : "error"}
                icon={hasTokens ? <ExternalIcon/> : <CloseIcon/>}
                title={hasTokens ? `Returning to ${activeDeployment().appName}` : "Sign-in failed"}
                message={hasTokens ? "Opening the app..." : "The sign-in callback did not include a handoff."}
                actions={nativeUrl && <a className="btn btn-primary" href={nativeUrl}>Open app</a>}
            />
        </div>
    );
}

function LoginApp() {
    useEffect(() => {
        captureReferralAttributionFromUrl();
    }, []);

    // Detect /login/done landing — magic link click landed here. The server
    // redirected with tokens in the URL fragment (existing user) OR with
    // ?signup=1 (new user, signup form needed).
    const initialFromMagic = useRef<{step: Step; email: string; userId?: number | null; loginId?: string | null} | null>(null);
    if (initialFromMagic.current === null && typeof window !== "undefined") {
        initialFromMagic.current = consumeMagicLanding();
    }

    // OAuth handoff: provider callback can land here with one of:
    //   ?provider_signup=<token>  → SPA pre-fills the deployment-specific signup form
    //   ?provider_error=<code>    → SPA shows styled error in the same card
    // (No more raw plaintext error pages.)
    const initialProviderSignupToken = useRef<string | null>(null);
    const initialProviderError = useRef<string | null>(null);
    if (initialProviderSignupToken.current === null && typeof window !== "undefined") {
        const params = new URLSearchParams(window.location.search);
        initialProviderSignupToken.current = params.get("provider_signup");
        initialProviderError.current = params.get("provider_error");
    }

    const [step, setStep] = useState<Step>(
        initialProviderError.current ? "provider-error" :
        initialProviderSignupToken.current ? "provider-signup" :
        initialFromMagic.current?.step ?? "email-entry"
    );
    const [email, setEmail] = useState(initialFromMagic.current?.email ?? "");
    // The start screen's one field ("mobile or email"; email only where there is no phone sign-in)
    // and the phone-only form behind "Continue with phone". Both keep the chip's country.
    const [start, setStart] = useState<ContactValue>(() => ({...initialContact(startAccept() ?? "email"), text: initialFromMagic.current?.email ?? ""}));
    const [phoneForm, setPhoneForm] = useState<ContactValue>(() => initialContact("phone"));
    // The number the pending code went to (E.164), and how: a text or a call.
    const [phone, setPhone] = useState("");
    const [phoneDelivery, setPhoneDelivery] = useState<"sms" | "call">("sms");
    const [voiceAvailable, setVoiceAvailable] = useState(false);
    // Which rail the pending code belongs to. The phone rail has no magic link, so its
    // code step neither polls nor mentions one.
    const [channel, setChannel] = useState<"email" | "sms">("email");
    const [resendDelay,setResendDelay]=useState(60);
    const [loginId, setLoginId] = useState<string | null>(initialFromMagic.current?.loginId ?? null);
    const [emailLoginNeedsSignup, setEmailLoginNeedsSignup] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [userId, setUserId] = useState<number | null>(initialFromMagic.current?.userId ?? null);
    const [pendingOAuth, setPendingOAuth] = useState<PendingOAuthProfile | null>(null);
    const [authReturnToOverride, setAuthReturnToOverride] = useState<string | null>(null);

    // Centralized post-auth handoff: tokens are saved by the time we get here.
    // If the platform supports a passkey and the user hasn't dismissed the
    // offer, intercept with the setup card; otherwise redirect immediately.
    async function completeAuth(uid: number | null, returnToOverride?: string | null) {
        setUserId(uid);
        setAuthReturnToOverride(returnToOverride ?? null);
        clearStoredReferralAttribution();
        // Gated on the deployment: offering enrolment where passkeys are disabled
        // strands the credential -- the sign-in button that would use it is hidden.
        if (uid !== null && activeDeployment().passkey.enabled && await shouldOfferPasskeySetup(uid)) {
            setStep("passkey-offer");
            return;
        }
        setStep("success");
        void redirectToReturnTo(returnToOverride);
    }

    // Magic-link landing already saved tokens in consumeMagicLanding(). Decide
    // passkey-offer vs immediate redirect once on mount.
    useEffect(() => {
        if (initialFromMagic.current?.step === "magic-landed") {
            void completeAuth(initialFromMagic.current.userId ?? null);
        }
        const token = initialProviderSignupToken.current;
        if (token) {
            fetchPendingOAuth(token).then(setPendingOAuth).catch(() => {
                setError("Sign-in session expired. Please try again.");
                setStep("email-entry");
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** Send a sign-in code to a number, as a text or a call. Throws the API error for the caller's copy. */
    async function sendPhoneCode(e164: string, delivery: "sms" | "call", turnstile?: string) {
        const result = await requestSmsCode(e164, delivery, turnstile);
        setPhone(e164);
        setPhoneDelivery(delivery);
        setVoiceAvailable(result.voice_available === true);
        setLoginId(result.login_id);
        setResendDelay(result.retry_after_seconds ?? 60);
        setEmailLoginNeedsSignup(result.next_step === "code_and_signup");
        setChannel("sms");
        setError(null);
        setStep("code-entry");
    }

    const backToStart = () => { setStep("email-entry"); setEmailLoginNeedsSignup(false); setError(null); };

    return (
        <div className="auth-container">
            <BrandMark/>
            <SlidePages step={step}>
                <StartScreen
                    contact={start}
                    setContact={setStart}
                    error={step === "email-entry" ? error : null}
                    setError={setError}
                    onEmailSent={(address, result) => {
                        setEmail(address);
                        setChannel("email");
                        setLoginId(result.login_id);
                        setResendDelay(result.retry_after_seconds ?? 60);
                        setEmailLoginNeedsSignup(result.next_step === "code_and_signup");
                        setError(null);
                        setStep("code-entry");
                    }}
                    sendPhoneCode={sendPhoneCode}
                    onPhoneForm={() => { setError(null); setStep("phone-entry"); }}
                    onPasskeySignedIn={completeAuth}
                />
                {step === "phone-entry" ? (
                    <PhoneEntry
                        contact={phoneForm}
                        setContact={setPhoneForm}
                        error={error}
                        setError={setError}
                        sendPhoneCode={sendPhoneCode}
                        onBack={backToStart}
                    />
                ) : (
                    <PostEmailPanel
                        resendDelay={resendDelay}
                        loginId={loginId}
                        email={email}
                        phone={phone}
                        phoneDelivery={phoneDelivery}
                        setPhoneDelivery={setPhoneDelivery}
                        voiceAvailable={voiceAvailable}
                        channel={channel}
                        step={step}
                        userId={userId}
                        setStep={setStep}
                        error={error}
                        setError={setError}
                        onBack={backToStart}
                        onAuthenticated={completeAuth}
                        authReturnToOverride={authReturnToOverride}
                        pendingOAuth={pendingOAuth}
                        emailLoginNeedsSignup={emailLoginNeedsSignup}
                        providerSignupToken={initialProviderSignupToken.current}
                        providerErrorCode={initialProviderError.current}
                    />
                )}
            </SlidePages>
        </div>
    );
}

// --- Sliding container -------------------------------------------------------
// Independently observes both panels' heights and animates the container to
// whichever is active. Surviving every content-replacement inside the post-
// email panel (code → signup → success) needs the observer to keep firing
// regardless of which step we're on.

function SlidePages({step, children}: {step: Step; children: [React.ReactNode, React.ReactNode]}) {
    const [emailNode, postEmailNode] = children;
    const showPost = step !== "email-entry";

    const panelARef = useRef<HTMLDivElement>(null);
    const panelBRef = useRef<HTMLDivElement>(null);
    const [activeHeight, setActiveHeight] = useState(0);

    // Synchronous measurement after every render (fires before browser paint
    // → no flash of stale height). useLayoutEffect plus a ResizeObserver
    // fallback for content that grows async after mount (font load, etc.).
    useLayoutEffect(() => {
        const measure = () => {
            const active = showPost ? panelBRef.current : panelARef.current;
            if (active) setActiveHeight(active.offsetHeight);
        };
        measure();
        const observers = [panelARef.current, panelBRef.current].map(el => {
            if (!el) return null;
            const ro = new ResizeObserver(measure);
            ro.observe(el);
            return ro;
        });
        return () => observers.forEach(ro => ro?.disconnect());
    }, [showPost, step]);

    return (
        <div className={`slide-pages ${showPost ? "show-post" : ""}`} style={activeHeight ? {height: activeHeight} : undefined}>
            <div className="slide-page slide-page-a" ref={panelARef}>{emailNode}</div>
            <div className="slide-page slide-page-b" ref={panelBRef}>{postEmailNode}</div>
        </div>
    );
}

// --- Page 1: the start screen ------------------------------------------------
// Providers first (Google leads), then "Continue with phone" in the same style (owner, 7 Oct), then
// the passkey row on a device that can use one, then the one field: "mobile or email" where the
// deployment has both, otherwise just the one it has. Spacing replaces the OR rules.

/** What the start screen's field accepts on this deployment. */
function startAccept(): ContactAccept | null {
    const cfg = activeDeployment();
    return cfg.emailEnabled && cfg.phone.enabled ? "any" : cfg.emailEnabled ? "email" : cfg.phone.enabled ? "phone" : null;
}

/** Whether this device has a fingerprint, face or screen-lock authenticator (the passkey row shows only then). */
function usePlatformAuthenticator(): boolean {
    const [available, setAvailable] = useState(false);
    useEffect(() => {
        let live = true;
        (async () => {
            try {
                const check = typeof PublicKeyCredential !== "undefined" ? PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable : undefined;
                const ok = check ? await check.call(PublicKeyCredential) : false;
                if (live) setAvailable(ok);
            } catch { /* no WebAuthn here: no passkey row */ }
        })();
        return () => { live = false; };
    }, []);
    return available;
}

/** The shared phone submit: checks the number, sends the code (a text or a call), maps refusals to words. */
function usePhoneSubmit(sendPhoneCode: (e164: string, delivery: "sms" | "call", turnstile?: string) => Promise<void>, setError: (s: string | null) => void) {
    const [submitting, setSubmitting] = useState(false);
    const human = useHumanCheck();
    async function submit(contact: ContactValue, delivery: "sms" | "call") {
        const e164 = contactE164(contact);
        if (!e164) { setError("Enter your mobile number."); return; }
        if (!phoneCountryAllowed(contact.country)) return;
        setSubmitting(true);
        setError(null);
        try { await human.guard(token => sendPhoneCode(e164, delivery, token)); }
        catch (err) { setError(err instanceof ApiError ? phoneErrorCopy(err) : "Network error. Please try again."); }
        finally { setSubmitting(false); }
    }
    return {submitting, submit, humanCheck: human.slot};
}

function phoneCountryAllowed(country: string): boolean {
    return phoneCountryAdmitted(activeDeployment().phone, country);
}

/** Said under the field when the chip names a country phone sign-in does not serve yet. */
function PhoneCountryHint({country}: {country: string}) {
    if (phoneCountryAllowed(country)) return null;
    const name = countryByCode(country)?.name ?? country;
    return <p className="hint" role="status">Phone sign-in isn't available in {name} yet.{orUseEmail()}</p>;
}

/** "Text me" and "Call me" (owner, 8 Oct: "default to call me if they press enter"). Call me is the
 *  submit, so Enter calls, wherever voice is enabled for the chip's country; elsewhere Text me is. */
function PhoneActions({country, submitting, onText}: {country: string; submitting: boolean; onText: () => void}) {
    const cfg = activeDeployment();
    const allowed = phoneCountryAllowed(country);
    const voice = allowed && (cfg.phone.voiceCountries ?? []).includes(country);
    if (!voice) {
        return <button type="submit" className="btn btn-primary" disabled={submitting || !allowed}>{submitting ? <span className="spinner"/> : "Text me"}</button>;
    }
    return (
        <div className="pair">
            <button type="button" className="btn" disabled={submitting} onClick={onText}>Text me</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? <span className="spinner"/> : "Call me"}</button>
        </div>
    );
}

function StartScreen({contact, setContact, error, setError, onEmailSent, sendPhoneCode, onPhoneForm, onPasskeySignedIn}: {
    contact: ContactValue;
    setContact: (v: ContactValue) => void;
    error: string | null;
    setError: (s: string | null) => void;
    onEmailSent: (email: string, result: RequestResult) => void;
    sendPhoneCode: (e164: string, delivery: "sms" | "call", turnstile?: string) => Promise<void>;
    onPhoneForm: () => void;
    onPasskeySignedIn: (userId: number) => void;
}) {
    const cfg = activeDeployment();
    const accept = startAccept();
    const [submitting, setSubmitting] = useState(false);
    const phoneSubmit = usePhoneSubmit(sendPhoneCode, setError);
    const [passkeyBusy, setPasskeyBusy] = useState(false);
    const passkeySupported = typeof window !== "undefined" && browserSupportsWebAuthn();
    const platformAuthenticator = usePlatformAuthenticator();
    const nativeExternalLoginUrl = getNativeExternalLoginUrl();
    const showPasskeyButton = cfg.passkey.enabled && (nativeExternalLoginUrl !== null || (passkeySupported && platformAuthenticator));
    const phoneMode = accept === "phone" || (accept === "any" && contact.phone);
    // Guard: prevent double sign-in if conditional mediation resolves at
    // the same instant the user clicks the explicit button.
    const passkeyDoneRef = useRef(false);

    function handlePasskeyResult(r: PasskeySignInResult) {
        if (passkeyDoneRef.current) return;
        passkeyDoneRef.current = true;
        saveTokens({access_token: r.accessToken, refresh_token: r.refreshToken});
        dismissPasskeySetup(r.userId);
        onPasskeySignedIn(r.userId);
    }

    async function passkeySignIn() {
        setPasskeyBusy(true);
        setError(null);
        if (!passkeySupported && nativeExternalLoginUrl) {
            launchNativeExternalAuth(nativeExternalLoginUrl);
            setPasskeyBusy(false);
            return;
        }
        try {
            const r = await signInWithPasskey();
            if (!r) { setError("Passkey sign-in unavailable."); return; }
            handlePasskeyResult(r);
        } catch (err: unknown) {
            // User cancellation → NotAllowedError → silent. Else: tell the
            // user what to do instead of a generic error.
            if (hasErrorName(err, "NotAllowedError")) return;
            if (hasErrorName(err, "SecurityError") && nativeExternalLoginUrl) {
                launchNativeExternalAuth(nativeExternalLoginUrl);
                return;
            }
            setError("No passkey on this device yet. Sign in another way, then set one up when asked.");
        } finally {
            setPasskeyBusy(false);
        }
    }

    // Conditional Mediation: starts a long-lived passkey request that
    // surfaces matching credentials inside the field's autofill
    // dropdown — no UI appears until the user picks one. Must be kicked
    // off on mount because the browser binds it to the form's lifecycle.
    // Cancellation (user typed an email instead, navigated away) just
    // rejects silently — no error to show.
    useEffect(() => {
        // Gated on the deployment too: one with passkeys disabled must not fire
        // the WebAuthn options request on mount (it 404s there).
        if (!cfg.passkey.enabled || !passkeySupported) return;
        let cancelled = false;
        (async () => {
            try {
                const r = await signInWithPasskey({useBrowserAutofill: true});
                if (cancelled || !r) return;
                handlePasskeyResult(r);
            } catch { /* user cancelled / no passkey picked — ignore */ }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function submitEmail(address: string) {
        setSubmitting(true);
        setError(null);
        try {
            onEmailSent(address, await requestEmailCode(address));
        } catch (err) {
            if (err instanceof ApiError) {
                if (err.code === "too_soon") setError(`Wait ${detailNumber(err.detail, "retry_after_seconds") ?? 60}s before requesting another code.`);
                else if (err.code === "too_many_from_ip") setError("Too many requests from this network. Try again in an hour.");
                else if (err.code === "email_delivery_blocked") setError(cfg.phone.enabled
                    ? "Email delivery to this address is blocked after a previous bounce or complaint. Use your mobile number, or a different email address."
                    : "Email delivery to this address is blocked after a previous bounce or complaint. Use a different email address or contact support.");
                else if (err.code === "email_delivery_unavailable") setError("We couldn't check or send your email just now. Try again shortly.");
                else if (err.code === "email_not_allowed") setError("This email address can't be used. Try a different one.");
                else if (err.code === "signup_challenge_required") setError("Extra verification is required for this signup. Please contact support.");
                else if (err.code === "invalid_email") setError("That doesn't look like a valid email.");
                else setError("Something went wrong. Please try again.");
            } else {
                setError("Network error. Please try again.");
            }
        } finally {
            setSubmitting(false);
        }
    }

    function submit(e: React.FormEvent) {
        e.preventDefault();
        if (submitting || phoneSubmit.submitting) return;
        if (phoneMode) {
            const voice = (cfg.phone.voiceCountries ?? []).includes(contact.country);
            void phoneSubmit.submit(contact, voice ? "call" : "sms");
            return;
        }
        const address = contact.text.trim();
        if (!isEmail(address)) {
            setError(!address ? (accept === "any" ? "Enter your email or mobile number." : "Enter your email address.") : "That doesn't look like a valid email.");
            return;
        }
        void submitEmail(address);
    }

    return (
        <form onSubmit={submit} noValidate>
            <Head title={signInHeading()}/>
            <div className="stack">
                {enabledProviders().map(p => (
                    <ProviderButton key={p} provider={p} label={`Continue with ${providerLabel(p)}`}/>
                ))}
                {accept === "any" && (
                    <button type="button" className="btn" onClick={onPhoneForm}><MobileIcon/><span>Continue with phone</span></button>
                )}
                {showPasskeyButton && (
                    <button type="button" className="btn" onClick={passkeySignIn} disabled={passkeyBusy}>
                        <PasskeyIcon/><span>Continue with passkey</span>
                    </button>
                )}
            </div>
            {accept && (
                <div className="entry">
                    <ContactField
                        accept={accept}
                        value={contact}
                        onChange={(v) => { setContact(v); if (error) setError(null); }}
                        id="login-contact"
                        placeholder={accept === "any" ? "Email or mobile number" : accept === "phone" ? "Mobile number" : "Email"}
                        invalid={!!error}
                        flagUrl={flagUrl}
                    />
                    {phoneMode && <PhoneCountryHint country={contact.country}/>}
                    {phoneMode && phoneSubmit.humanCheck}
                    {error && <div className="error-msg" role="alert">{error}</div>}
                    {phoneMode
                        ? <PhoneActions country={contact.country} submitting={phoneSubmit.submitting} onText={() => void phoneSubmit.submit(contact, "sms")}/>
                        : <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? <span className="spinner"/> : "Continue"}</button>}
                </div>
            )}
            {!accept && error && <div className="error-msg" role="alert">{error}</div>}
        </form>
    );
}

// --- The phone-only form ("Continue with phone") ------------------------------

function PhoneEntry({contact, setContact, error, setError, sendPhoneCode, onBack}: {
    contact: ContactValue;
    setContact: (v: ContactValue) => void;
    error: string | null;
    setError: (s: string | null) => void;
    sendPhoneCode: (e164: string, delivery: "sms" | "call", turnstile?: string) => Promise<void>;
    onBack: () => void;
}) {
    const phoneSubmit = usePhoneSubmit(sendPhoneCode, setError);
    const voice = (activeDeployment().phone.voiceCountries ?? []).includes(contact.country);
    return (
        <form noValidate onSubmit={(e) => { e.preventDefault(); if (!phoneSubmit.submitting) void phoneSubmit.submit(contact, voice ? "call" : "sms"); }}>
            <Head title={signInHeading()} onBack={onBack}/>
            <div className="stack">
                <ContactField
                    accept="phone"
                    value={contact}
                    onChange={(v) => { setContact(v); if (error) setError(null); }}
                    id="login-phone"
                    placeholder="Mobile number"
                    autoFocus
                    invalid={!!error}
                    flagUrl={flagUrl}
                />
                <PhoneCountryHint country={contact.country}/>
                {phoneSubmit.humanCheck}
                {error && <div className="error-msg" role="alert">{error}</div>}
                <PhoneActions country={contact.country} submitting={phoneSubmit.submitting} onText={() => void phoneSubmit.submit(contact, "sms")}/>
            </div>
        </form>
    );
}

/** " Use email instead." where this deployment has email sign-in; nothing where it has none. */
function orUseEmail(): string {
    return activeDeployment().emailEnabled ? " Use email instead." : "";
}

/** User-facing copy for phone-rail refusals (server codes from src/login/sms.ts). */
function phoneErrorCopy(err: ApiError): string {
    switch (err.code) {
        case "invalid_phone": return "Enter your number with its country code, e.g. +44 7700 900123.";
        case "unsupported_country": return "Phone sign-in isn't available for that country yet. Use email instead.";
        case "voip_blocked": return "That looks like an internet (VoIP) number. Please use a mobile number.";
        case "unsupported_line_type": return "Please use a mobile number.";
        case "phone_opted_out": return `This number has opted out of our texts (it replied STOP).${orUseEmail()}`;
        case "too_soon": return `Wait ${detailNumber(err.detail, "retry_after_seconds") ?? 60}s before requesting another code.`;
        case "delivery_blocked": return `We couldn't text a code to this number.${orUseEmail()}`;
        case "call_unavailable": return "We can't call this number. Choose Text me instead.";
        case "human_check_required":
        case "human_check_failed": return "The browser check didn't pass. Please try again.";
        case "human_check_unavailable": return `The browser check couldn't load. Try again${activeDeployment().emailEnabled ? ", or use email instead" : ""}.`;
        case "use_email_instead": return `We can't send a code to this number.${orUseEmail()}`;
        case "phone_check_unavailable": return `Phone sign-in isn't available for this number right now.${orUseEmail()}`;
        case "lookup_unavailable":
        case "delivery_unavailable":
        case "send_failed": return `We couldn't send a code just now. Try again in a minute${activeDeployment().emailEnabled ? ", or use email instead" : ""}.`;
        default:
            if (err.code.startsWith("too_many")) return "Too many codes requested. Try again later.";
            return "Something went wrong. Please try again.";
    }
}

// Button copy derived from the provider id -- the config carries ids, not
// labels, so a deployment that enables a provider gets its label for free.

function ProviderButton({provider, label, disabled, comingSoon}: {provider: string; label: string; disabled?: boolean; comingSoon?: boolean}) {
    function start() {
        // Forward the SPA's return_to (where the email-code path also lands)
        // straight through to the provider start endpoint. getReturnTo()
        // also preserves the first valid return target across provider
        // errors/retries where the current URL no longer has ?return_to=.
        const returnTo = getReturnTo().url;
        if (provider === "google" && getNativePlatform() === "android" && isNativeProviderLoginReturnTo(returnTo)) {
            const nativeProviderLogin = buildNativeProviderLoginUrl(provider, returnTo);
            if (nativeProviderLogin !== null) {
                const nativeProviderLoginUrl = new URL(nativeProviderLogin);
                appendReferralParams(nativeProviderLoginUrl.searchParams);
                launchNativeProviderLogin(nativeProviderLoginUrl.toString());
                return;
            }
        }

        const nativeProviderUrl = buildNativeExternalProviderStartUrl(provider, returnTo);
        if (nativeProviderUrl !== null) {
            launchNativeExternalAuth(nativeProviderUrl);
            return;
        }

        const url = new URL(`/oauth/${provider}/start`, window.location.origin);
        url.searchParams.set("return_to", returnTo);
        appendReferralParams(url.searchParams);
        window.location.href = `${url.pathname}${url.search}`;
    }
    return (
        <button
            type="button"
            className={`btn btn-provider-${provider}`}
            disabled={disabled}
            title={disabled ? "Coming soon" : undefined}
            onClick={() => { if (!disabled) start(); }}
        >
            <ProviderIcon provider={provider}/>
            <span>{label}</span>
            {comingSoon && <span className="hint">Coming soon</span>}
        </button>
    );
}

function buildNativeExternalProviderStartUrl(provider: string, returnTo: string): string | null {
    return buildNativeExternalAuthUrl(`/oauth/${provider}/start`, returnTo)?.toString() ?? null;
}

function getNativeExternalLoginUrl(): string | null {
    const nativePlatform = getNativePlatform();
    if (nativePlatform === null) return null;
    if (new URLSearchParams(window.location.search).get("native_external") === "1") return null;

    const url = buildNativeExternalAuthUrl("/login/", getReturnTo().url, nativePlatform);
    if (url === null) return null;

    url.searchParams.set("native_platform", nativePlatform);
    url.searchParams.set("native_external", "1");
    return url.toString();
}

function buildNativeExternalAuthUrl(path: string, returnTo: string, nativePlatform: string | null = getNativePlatform()): URL | null {
    if (nativePlatform === null) return null;

    const nativeReturnTo = buildNativeExternalLoginReturnTo(returnTo);
    if (nativeReturnTo === null) return null;

    const url = new URL(path, window.location.origin);
    url.searchParams.set("return_to", nativeReturnTo);
    appendReferralParams(url.searchParams);
    return url;
}

function launchNativeProviderLogin(url: string) {
    try {
        if (window.top && window.top !== window) {
            window.top.location.href = url;
            return;
        }
    } catch {
        /* Cross-frame navigation can be restricted; same-frame is still a valid fallback. */
    }

    window.location.href = url;
}

function launchNativeExternalAuth(url: string) {
    const parentOrigin = getNativeParentOrigin(getReturnTo().url);
    try {
        if (window.parent && window.parent !== window) {
            window.parent.postMessage({type: AUTH_EXTERNAL_OPEN_MESSAGE_TYPE, url}, parentOrigin);
            return;
        }
    } catch {
        /* Cross-frame messaging can be restricted; same-frame is still a valid fallback. */
    }

    window.location.href = url;
}

function ProviderIcon({provider}: {provider: string}) {
    // Brand-mark SVGs inline so we don't depend on icon font / image hosting.
    if (provider === "google") return (
        <svg viewBox="0 0 48 48" width="20" height="20" aria-hidden="true">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8a12 12 0 1 1 0-24c3.1 0 5.9 1.1 8 3l5.7-5.7A20 20 0 1 0 24 44c11 0 20-9 20-20 0-1.3-.1-2.3-.4-3.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.1 8 3l5.7-5.7A20 20 0 0 0 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.2A12 12 0 0 1 12.7 28l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4 5.6l6.2 5.2C41.3 35.3 44 30 44 24c0-1.3-.1-2.3-.4-3.5z"/>
        </svg>
    );
    if (provider === "apple") return (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
        </svg>
    );
    if (provider === "discord") return (
        <svg viewBox="0 0 24 24" width="20" height="20" fill="#5865F2" aria-hidden="true">
            <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.245.198.372.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
        </svg>
    );
    if (provider === "instagram") return (
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
            <path d="M7.75 2h8.5A5.76 5.76 0 0 1 22 7.75v8.5A5.76 5.76 0 0 1 16.25 22h-8.5A5.76 5.76 0 0 1 2 16.25v-8.5A5.76 5.76 0 0 1 7.75 2Zm0 2A3.75 3.75 0 0 0 4 7.75v8.5A3.75 3.75 0 0 0 7.75 20h8.5A3.75 3.75 0 0 0 20 16.25v-8.5A3.75 3.75 0 0 0 16.25 4h-8.5ZM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6Zm5.25-2.35a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2Z"/>
        </svg>
    );
    if (provider === "spotify") return (
        <svg viewBox="0 0 24 24" width="20" height="20" fill="#1DB954" aria-hidden="true">
            <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12C24 5.4 18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/>
        </svg>
    );
    return null;
}

// --- Page 2 (multi-state): code entry → signup → success --------------------

function PostEmailPanel({resendDelay, loginId, email, phone, phoneDelivery, setPhoneDelivery, voiceAvailable, channel, step, userId, setStep, error, setError, onBack, onAuthenticated, authReturnToOverride, pendingOAuth, emailLoginNeedsSignup, providerSignupToken, providerErrorCode}: {
    resendDelay:number;
    loginId: string | null;
    email: string;
    phone: string;
    phoneDelivery: "sms" | "call";
    setPhoneDelivery: (d: "sms" | "call") => void;
    voiceAvailable: boolean;
    channel: "email" | "sms";
    step: Step;
    userId: number | null;
    setStep: (s: Step) => void;
    error: string | null;
    setError: (s: string | null) => void;
    onBack: () => void;
    onAuthenticated: (userId: number | null, returnToOverride?: string | null) => void;
    authReturnToOverride: string | null;
    pendingOAuth: PendingOAuthProfile | null;
    emailLoginNeedsSignup: boolean;
    providerSignupToken: string | null;
    providerErrorCode: string | null;
}) {
    if (step === "email-entry" || step === "phone-entry") return <div/>;

    if (step === "passkey-offer") {
        return <PasskeyOffer userId={userId} onDone={() => { setStep("success"); void redirectToReturnTo(authReturnToOverride); }}/>;
    }

    if (step === "provider-error") {
        return <ResultPanel
            variant="error"
            icon={<CloseIcon/>}
            title="Couldn't sign you in"
            message={providerErrorCopy(providerErrorCode)}
            actions={
                <button type="button" className="btn btn-primary" onClick={() => {
                    // Clear ?provider_error from the URL so a refresh doesn't re-show it
                    window.history.replaceState(null, "", window.location.pathname);
                    setStep("email-entry");
                }}>
                    Try again
                </button>
            }
        />;
    }

    if (step === "provider-signup") {
        // Wait for the pending profile fetch before rendering the form, so
        // Provider profile lands pre-filled (no flash of
        // empty fields). Plain subtitle, NOT ResultPanel — the green success
        // styling was flashing like "You're in" before the form appeared.
        if (!pendingOAuth || !providerSignupToken) {
            return <div className="loading-row" aria-label="Loading"><span className="spinner"/></div>;
        }
        if (pendingOAuth.email === null) {
            return <EmailVerifiedProviderSignupForm
                pendingOAuth={pendingOAuth}
                token={providerSignupToken}
                onDone={onAuthenticated}
                setError={setError}
                error={error}
            />;
        }
        return <SignupForm
            email={pendingOAuth.email}
            suggestedUserName={pendingOAuth.suggestedUserName}
            oauth={{token: providerSignupToken, firstName: pendingOAuth.firstName, lastName: pendingOAuth.lastName}}
            onDone={onAuthenticated}
            setError={setError}
            error={error}
        />;
    }

    if (step === "magic-landed" || step === "success") {
        // For first-party (external) return_to we briefly show "You're in"
        // before the redirect lands. For OAuth-flow return_to (internal),
        // the redirect to /oauth2/authorize is instant and the consent page
        // renders next — showing "You're in" first would just be a flash.
        if (isInternalReturnTo()) return <div/>;
        return <ResultPanel icon={<CheckIcon/>} title="You're in" message="Redirecting…"/>;
    }

    if (step === "signup") {
        return <SignupForm loginId={loginId} email={email} oauth={null} onDone={onAuthenticated} setError={setError} error={error}/>;
    }

    return (
        <CodeEntry
            resendDelay={resendDelay}
            email={email}
            phone={phone}
            phoneDelivery={phoneDelivery}
            setPhoneDelivery={setPhoneDelivery}
            voiceAvailable={voiceAvailable}
            channel={channel}
            loginId={loginId!}
            error={error}
            setError={setError}
            onBack={onBack}
            onAuthenticated={onAuthenticated}
            needsSignup={emailLoginNeedsSignup}
            onRequiresSignup={() => setStep("signup")}
        />
    );
}

interface SignupDetailsValue {
    displayName: string;
    firstName: string;
    lastName: string;
    userName: string;
    dateOfBirth: string;
    /** Forge asks for an age (digits) by default; a full birthday is optional. */
    age: string;
    /** Forge only: the person chose to enter their full birthday instead of an age. */
    birthdayMode: boolean;
    tosAccepted: boolean;
}

function initialSignupDetails(email: string | null, suggestedUserName?: string, defaults?: Partial<SignupDetailsValue>): SignupDetailsValue {
    return {
        displayName: defaults?.displayName ?? [defaults?.firstName, defaults?.lastName].filter(Boolean).join(" "),
        firstName: defaults?.firstName ?? "",
        lastName: defaults?.lastName ?? "",
        userName: activeDeployment().signupProfile === "display-name" ? "" : defaults?.userName ?? defaultUserNameFromEmail(email, suggestedUserName),
        dateOfBirth: defaults?.dateOfBirth ?? "",
        age: defaults?.age ?? "",
        birthdayMode: defaults?.birthdayMode ?? false,
        tosAccepted: defaults?.tosAccepted ?? false,
    };
}

function signupDetailsInput(details: SignupDetailsValue): SignupInput {
    return activeDeployment().signupProfile === "display-name"
        ? details.birthdayMode
            ? {kind: "display-name", displayName: details.displayName, dateOfBirth: details.dateOfBirth, tosAccepted: details.tosAccepted}
            : {kind: "display-name", displayName: details.displayName, age: details.age, tosAccepted: details.tosAccepted}
        : {kind: "first-last-username", firstName: details.firstName, lastName: details.lastName, userName: details.userName,
            dateOfBirth: details.dateOfBirth, tosAccepted: details.tosAccepted};
}

function signupDetailsAreValid(details: SignupDetailsValue): boolean {
    return signupInputIsValid(signupDetailsInput(details), activeDeployment());
}

const SIGNUP_PROBLEM_COPY: Record<SignupProfileError, string> = {
    display_name_required: "Enter your name.",
    first_and_last_name_required: "Enter your first and last name.",
    invalid_name: "Enter a valid name (up to 64 characters).",
    invalid_username: "Usernames need at least 3 letters, numbers, dots, dashes or underscores.",
    date_of_birth_required: "Enter your date of birth.",
    invalid_date_of_birth: "That date of birth doesn't look right. Please check it.",
    age_required: "Enter your age in years.",
    invalid_age: "Enter your age in years, as a number.",
    terms_required: "Tick the box to accept the Terms and Privacy Policy.",
};

/** The first thing stopping this signup, in words, or null when it can be submitted. */
function signupDetailsProblem(details: SignupDetailsValue): string | null {
    const problem = signupInputProblem(signupDetailsInput(details), activeDeployment());
    return problem === null ? null : SIGNUP_PROBLEM_COPY[problem];
}

function defaultUserNameFromEmail(email: string | null, suggestedUserName?: string): string {
    return suggestedUserName ? toUserNameSuggestion(suggestedUserName) : userNameSuggestionFromEmail(email);
}

function userNameValidationMessage(userName: string): string | null {
    const error = getUserNameValidationError(userName);
    if (error === null || error === "empty") return null;
    return userNameErrorMessage(error);
}

function userNameErrorMessage(error: UserNameValidationError): string {
    switch (error) {
        case "empty": return "";
        case "too_short": return "Usernames need at least 3 characters.";
        case "too_long": return `Usernames can be up to ${USER_NAME_MAX_LENGTH} characters.`;
        case "invalid_chars": return "Use only letters, numbers, dots, dashes, or underscores.";
        default: return "";
    }
}

function SignupDetailsFields({details, setDetails, showNameFields, autoFocusUserName, autoFocusFirstName}: {
    details: SignupDetailsValue;
    setDetails: (details: SignupDetailsValue) => void;
    showNameFields: boolean;
    autoFocusUserName: boolean;
    autoFocusFirstName: boolean;
}) {
    const displayNameOnly = activeDeployment().signupProfile === "display-name";
    const userNameError = displayNameOnly ? null : userNameValidationMessage(details.userName);
    return (
        <>
            {displayNameOnly ? (
                <div className="field">
                    <label className="label" htmlFor="signup-display-name">Name</label>
                    <input id="signup-display-name" className="input"
                           value={details.displayName}
                           onChange={(e) => setDetails({...details, displayName: e.target.value})}
                           autoFocus={autoFocusFirstName || autoFocusUserName}
                           required maxLength={SIGNUP_NAME_MAX_LENGTH} autoComplete="name"/>
                </div>
            ) : <div className="field">
                <label className="label" htmlFor="signup-username">Username</label>
                <input id="signup-username" className="input"
                       name="twoshot-signup-handle"
                       value={details.userName}
                       onChange={(e) => setDetails({...details, userName: normaliseSignupUserNameInput(e.target.value)})}
                       onPaste={(e) => {
                           const pasted = e.clipboardData.getData("text").trim();
                           if (!isEmailAddress(pasted)) return;
                           e.preventDefault();
                           setDetails({...details, userName: userNameSuggestionFromEmail(pasted)});
                       }}
                       required minLength={3} maxLength={254}
                       placeholder="Pick a username"
                       autoFocus={autoFocusUserName}
                       autoComplete="off"
                       autoCorrect="off"
                       autoCapitalize="none"
                       spellCheck={false}
                       aria-invalid={userNameError !== null}/>
                {userNameError && <div className="error-msg" role="alert">{userNameError}</div>}
            </div>}
            {!displayNameOnly && showNameFields && (
                <>
                    <div className="field">
                        <label className="label" htmlFor="signup-first">First name</label>
                        <input id="signup-first" className="input"
                               value={details.firstName}
                               onChange={(e) => setDetails({...details, firstName: e.target.value})}
                               autoFocus={autoFocusFirstName} required maxLength={SIGNUP_NAME_MAX_LENGTH} autoComplete="given-name"/>
                    </div>
                    <div className="field">
                        <label className="label" htmlFor="signup-last">Last name</label>
                        <input id="signup-last" className="input"
                               value={details.lastName}
                               onChange={(e) => setDetails({...details, lastName: e.target.value})}
                               required maxLength={64} autoComplete="family-name"/>
                    </div>
                </>
            )}
            {/* Age is collected on every signup path (incl. OAuth, where the provider does not
                supply it) as the age-band data source. Forge asks for a plain age; TwoShot keeps
                its date of birth. The server derives the same age band from either. */}
            {displayNameOnly && !details.birthdayMode ? (
                <div className="field">
                    <label className="label" htmlFor="signup-age">Age</label>
                    <input id="signup-age" className="input"
                           type="text"
                           inputMode="numeric"
                           pattern="[0-9]*"
                           maxLength={3}
                           value={details.age}
                           onChange={(e) => setDetails({...details, age: e.target.value.replace(/\D/g, "").slice(0, 3)})}
                           required
                           autoComplete="off"
                           aria-invalid={details.age !== "" && parseAgeYears(details.age) === null}/>
                    {details.age !== "" && parseAgeYears(details.age) === null && (
                        <div className="error-msg" role="alert">Enter your age in years.</div>
                    )}
                    <button type="button" className="btn btn-ghost btn-md inline-link" onClick={() => setDetails({...details, birthdayMode: true})}>
                        Enter full birthday instead
                    </button>
                </div>
            ) : (
                <div className="field">
                    <label className="label" htmlFor="signup-dob">Date of birth</label>
                    <input id="signup-dob" className="input"
                           type="date"
                           value={details.dateOfBirth}
                           onChange={(e) => setDetails({...details, dateOfBirth: e.target.value})}
                           max={new Date().toISOString().slice(0, 10)}
                           required
                           autoComplete="bday"/>
                    {displayNameOnly && (
                        <button type="button" className="btn btn-ghost btn-md inline-link" onClick={() => setDetails({...details, birthdayMode: false})}>
                            Enter age instead
                        </button>
                    )}
                </div>
            )}
            {/* The whole row is the tap target (iOS in-app browsers: WhatsApp, Instagram).
                The links stop propagation so opening the Terms never toggles the box. */}
            <label className="checkbox-row" htmlFor="signup-terms">
                <input id="signup-terms" type="checkbox" className="check" checked={details.tosAccepted}
                       onChange={(e) => setDetails({...details, tosAccepted: e.target.checked})}/>
                <span>I agree to the <a href={activeDeployment().termsUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>Terms</a> and <a href={activeDeployment().privacyUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>Privacy Policy</a>.</span>
            </label>
        </>
    );
}

// Shared 6-digit code input. Handles paste normalisation (strips spaces /
// dashes before the digit-check so "123 456" and "123-456" work) and calls
// onComplete once per distinct 6-digit value, guarding against re-fire on the
// same value so a wrong-code error never burns a retry automatically.
function CodeInput({id, value, onChange, onComplete, disabled, autoFocus}: {
    id: string;
    value: string;
    onChange: (v: string) => void;
    onComplete: (v: string) => void;
    disabled?: boolean;
    autoFocus?: boolean;
}) {
    const lastFiredRef = useRef<string | null>(null);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        // Strip non-digits FIRST (handles paste of "123 456" / "123-456"),
        // then cap to 6. Dropping maxLength from the DOM element means the
        // browser doesn't truncate before our strip runs.
        const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
        onChange(digits);
        if (digits.length === 6 && digits !== lastFiredRef.current) {
            lastFiredRef.current = digits;
            onComplete(digits);
        }
        // If user edits away from 6 digits, reset the guard so re-entry fires.
        if (digits.length < 6) lastFiredRef.current = null;
    }, [onChange, onComplete]);

    return (
        <input
            id={id}
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            autoComplete="one-time-code"
            placeholder="000000"
            value={value}
            onChange={handleChange}
            disabled={disabled}
            autoFocus={autoFocus}
            required
            className="code-input"
        />
    );
}

function CodeEntry({resendDelay, email, phone, phoneDelivery, setPhoneDelivery, voiceAvailable, channel, loginId, error, setError, onBack, onAuthenticated, needsSignup, onRequiresSignup}: {
    resendDelay:number;
    email: string;
    phone: string;
    phoneDelivery: "sms" | "call";
    setPhoneDelivery: (d: "sms" | "call") => void;
    voiceAvailable: boolean;
    channel: "email" | "sms";
    loginId: string;
    error: string | null;
    setError: (s: string | null) => void;
    onBack: () => void;
    onAuthenticated: (userId: number | null, returnToOverride?: string | null) => void;
    needsSignup: boolean;
    onRequiresSignup: () => void;
}) {
    const [code, setCode] = useState("");
    const [codeVerifiedByMagic, setCodeVerifiedByMagic] = useState(false);
    const [signupDetails, setSignupDetails] = useState(() => initialSignupDetails(email));
    const [submitting, setSubmitting] = useState(false);
    const [resendAt, setResendAt] = useState(() => Date.now() + resendDelay*1000);
    const [resending, setResending] = useState(false);
    const [clock, setClock] = useState(Date.now);
    useEffect(() => { const timer = setInterval(() => setClock(Date.now()), 1000); return () => clearInterval(timer); }, []);
    const remaining = Math.max(0, Math.ceil((resendAt - clock) / 1000));

    const submittingRef = useRef(false);

    useEffect(() => {
        submittingRef.current = submitting;
    }, [submitting]);

    // Cross-device polling: if the user clicks the magic link on another
    // device, this poll picks up the completion and finishes login here.
    useEffect(() => {
        // The phone rail has no magic link, so there is nothing to poll for.
        if (channel === "sms") return;
        let cancelled = false;
        const interval = setInterval(async () => {
            try {
                if (submittingRef.current) return;
                const r = await pollLogin(loginId);
                if (cancelled) return;
                if (r.status === "completed_existing_user") {
                    saveTokens(r);
                    clearInterval(interval);
                    onAuthenticated(r.user_id);
                } else if (r.status === "completed_new_user") {
                    if (needsSignup) {
                        setCodeVerifiedByMagic(true);
                        setError(null);
                    } else {
                        onRequiresSignup();
                    }
                    clearInterval(interval);
                } else if (r.status === "expired") {
                    setError("Code expired. Request a new one.");
                    clearInterval(interval);
                }
            } catch { /* transient — keep polling */ }
        }, 2_000);
        return () => { cancelled = true; clearInterval(interval); };
    }, [channel, loginId, needsSignup, onAuthenticated, onRequiresSignup, setError]);

    async function doSubmit(codeArg: string, explicit = false) {
        const currentValid = (codeVerifiedByMagic || codeArg.length >= 6) && (!needsSignup || signupDetailsAreValid(signupDetails));
        if (submittingRef.current) return;
        if (!currentValid) {
            // Typing the last digit auto-submits; only a press of the button explains what is missing.
            if (explicit) setError(!(codeVerifiedByMagic || codeArg.length >= 6) ? "Enter the 6-digit code."
                : signupDetailsProblem(signupDetails) ?? "Please complete the form.");
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            if (needsSignup && codeVerifiedByMagic) {
                const r = await completeSignup(signupDetailsInput(signupDetails), loginId);
                saveTokens(r);
                onAuthenticated(r.user_id);
                return;
            }

            const signup = needsSignup ? signupDetailsInput(signupDetails) : undefined;
            const r = channel === "sms"
                ? await verifySmsCode({loginId, phone: phone.trim(), code: codeArg, signup})
                : await verifyEmailCode(signup ? {code: codeArg, loginId, signup} : {code: codeArg, loginId});
            if (r.status !== "completed_new_user") {
                saveTokens({access_token: r.access_token, refresh_token: r.refresh_token});
                onAuthenticated(r.user_id);
            } else {
                onRequiresSignup();
            }
        } catch (err) {
            if (err instanceof ApiError) {
                if (err.code === "wrong_code") {
                    const remaining = detailNumber(err.detail, "attempts_remaining") ?? 0;
                    setError(remaining > 0 ? `Wrong code. ${remaining} ${remaining === 1 ? "try" : "tries"} left.` : "Wrong code. Request a new one.");
                } else if (err.code === "expired") setError("Code expired. Request a new one.");
                else if (err.code === "verification_unavailable") setError("We couldn't check your code just now. Try the same code again in a moment.");
                else if (err.code === "too_many_attempts") setError("Too many attempts. Request a new code.");
                else if (err.code === "username_taken") setError("That username is already taken. Try another.");
                else if (err.code === "display_name_required" || err.code === "invalid_name") setError("Please enter a valid name (up to 64 characters).");
                else if (err.code === "first_and_last_name_required") setError("Please enter your first and last name.");
                else if (err.code === "terms_required") setError("Please accept the Terms and Privacy Policy.");
                else if (err.code === "date_of_birth_required") setError("Please enter your date of birth.");
                else if (err.code === "age_required" || err.code === "invalid_age") setError("Please enter your age in years.");
                else if (err.code === "invalid_date_of_birth") setError("That date of birth doesn't look right. Please check it.");
                else if (err.code === "invalid_username") setError("Usernames need letters, numbers, dots, dashes, or underscores.");
                else if (err.code === "email_not_allowed") setError("This email address can't be used. Try a different one.");
                else if (err.code === "signup_challenge_required") setError("Extra verification is required for this signup. Please contact support.");
                else if (err.code === "login_not_pending_signup") setError("Sign-in session expired. Please try signing in again.");
                else setError("Couldn't verify. Try again.");
            } else {
                setError("Network error. Please try again.");
            }
        } finally {
            setSubmitting(false);
        }
    }

    function submit(e: React.FormEvent) {
        e.preventDefault();
        doSubmit(code, true);
    }

    async function callInstead() {
        setResending(true); setError(null);
        try {
            const sent = await callSmsCode(phone.trim(), loginId);
            setPhoneDelivery("call");
            setResendAt(Date.now() + sent.retry_after_seconds * 1000);
        } catch (err) {
            if (err instanceof ApiError) {
                const delay = detailNumber(err.detail, "retry_after_seconds"); if (delay) setResendAt(Date.now() + delay * 1000);
                setError(err.code === "too_soon" ? `Wait ${delay ?? 60}s, then we can call.` : err.code === "call_unavailable" ? "We can't call this number." : phoneErrorCopy(err));
            } else setError("Network error. Please try again.");
        } finally { setResending(false); }
    }

    const shownPhone = formatE164(phone);
    return (
        <form onSubmit={submit} noValidate>
            <Head title={needsSignup ? "Create your account" : signInHeading()} onBack={onBack}/>
            <p className="sent">
                {needsSignup && codeVerifiedByMagic
                    ? <>Code verified.</>
                    : channel === "sms"
                    ? (phoneDelivery === "call"
                        ? <>We're calling <strong>{shownPhone}</strong> to read you a code</>
                        : <>We texted a code to <strong>{shownPhone}</strong></>)
                    : <>We emailed a code to <strong>{email}</strong>. The link in it works too.</>}
            </p>
            <div className="stack">
            {!codeVerifiedByMagic && (
                <CodeInput
                    id="email-code"
                    value={code}
                    onChange={setCode}
                    onComplete={(v) => doSubmit(v)}
                    disabled={submitting}
                    autoFocus
                />
            )}
            {needsSignup && (
                <SignupDetailsFields
                    details={signupDetails}
                    setDetails={setSignupDetails}
                    showNameFields={true}
                    autoFocusUserName={false}
                    autoFocusFirstName={false}
                />
            )}
            {error && <div className="error-msg" role="alert">{error}</div>}
            <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? <span className="spinner"/> : (needsSignup ? "Create account" : "Verify")}
            </button>
            </div>
            <div className="links">
            <button type="button" className="btn btn-ghost btn-md" disabled={resending || remaining > 0} onClick={async () => {
                setResending(true); setError(null);
                try { const sent = await resendLoginCode(loginId); setResendAt(Date.now() + sent.retry_after_seconds * 1000); }
                catch (err) { if (err instanceof ApiError) {
                    const delay = detailNumber(err.detail,"retry_after_seconds"); if (delay) setResendAt(Date.now()+delay*1000);
                    setError(err.code === "delivery_limit" || err.code === "delivery_finished" ? "This code can't be resent. Start again for a new code."
                        : err.code === "email_delivery_blocked" ? "Email delivery is blocked. Go back and use phone sign-in or a different email."
                        : channel === "sms" && (err.code === "phone_opted_out" || err.code === "delivery_blocked") ? phoneErrorCopy(err)
                        : channel === "sms" ? `We couldn't resend just now. Wait a moment and try again${activeDeployment().emailEnabled ? ", or use email instead" : ""}.`
                        : "We couldn't resend just now. Wait a moment and try again.");
                } else setError("Network error. Please try again."); } finally { setResending(false); }
            }}>{resending ? "Requesting…" : remaining ? `Resend in ${remaining}s` : "Resend code"}</button>
            {channel === "sms" && voiceAvailable && phoneDelivery === "sms" && (
                <button type="button" className="btn btn-ghost btn-md" disabled={resending || remaining > 0} onClick={() => void callInstead()}>Call me instead</button>
            )}
            {channel === "sms" && activeDeployment().emailEnabled && (
                <button type="button" className="btn btn-ghost btn-md" onClick={onBack}>Use email instead</button>
            )}
            </div>
        </form>
    );
}

// Single signup form covers email/phone and OAuth. Provider names are editable
// prefills, including providers that supply no family name. OAuth submits with the
// stash token). Keeping one component means both flows always render with
// the same heading/subtitle/label/TOS markup and CSS — no drift between
// variants.
type EmailVerifiedProviderSignupPhase = "email" | "code" | "signup";

function EmailVerifiedProviderSignupForm({pendingOAuth, token, onDone, setError, error}: {
    pendingOAuth: PendingOAuthProfile;
    token: string;
    onDone: (userId: number | null, returnToOverride?: string | null) => void;
    setError: (s: string | null) => void;
    error: string | null;
}) {
    const [phase, setPhase] = useState<EmailVerifiedProviderSignupPhase>("email");
    const [providerEmail, setProviderEmail] = useState("");
    const [code, setCode] = useState("");
    const [providerLoginId, setProviderLoginId] = useState<string | null>(null);
    const [details, setDetails] = useState(() => initialSignupDetails(providerEmail, pendingOAuth.suggestedUserName, {
        firstName: pendingOAuth.firstName,
        lastName: pendingOAuth.lastName,
    }));
    const [submitting, setSubmitting] = useState(false);
    const submittingRef = useRef(false);

    useEffect(() => { submittingRef.current = submitting; }, [submitting]);


    async function linkAndFinish(userId: number, accessToken: string) {
        const linked = await linkPendingOAuth({token, accessToken});
        onDone(userId, linked.return_to);
    }

    async function doSubmit(codeArg: string, explicit = false) {
        const currentValid = phase === "email"
            ? isEmailAddress(providerEmail.trim())
            : phase === "code"
            ? codeArg.length >= 6
            : signupDetailsAreValid(details);
        if (submittingRef.current) return;
        if (!currentValid) {
            if (explicit) setError(phase === "email" ? "Enter a valid email address."
                : phase === "code" ? "Enter the 6-digit code."
                : signupDetailsProblem(details) ?? "Please complete the form.");
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            if (phase === "email") {
                const sent = await requestEmailCode(providerEmail.trim());
                setProviderLoginId(sent.login_id);
                setPhase("code");
                return;
            }
            if (phase === "code") {
                const r = await verifyEmailCode({code: codeArg, loginId: providerLoginId ?? undefined});
                if (r.status === "completed_new_user") {
                    setPhase("signup");
                    return;
                }
                saveTokens({access_token: r.access_token, refresh_token: r.refresh_token});
                await linkAndFinish(r.user_id, r.access_token);
                return;
            }
            const r = await completeSignup(signupDetailsInput(details), providerLoginId ?? undefined);
            saveTokens(r);
            await linkAndFinish(r.user_id, r.access_token);
        } catch (err) {
            if (err instanceof ApiError) {
                if (err.code === "too_soon") setError(`Wait ${detailNumber(err.detail, "retry_after_seconds") ?? 60}s before requesting another code.`);
                else if (err.code === "too_many_from_ip") setError("Too many requests from this network. Try again in an hour.");
                else if (err.code === "invalid_email") setError("That doesn't look like a valid email.");
                else if (err.code === "wrong_code") {
                    const remaining = detailNumber(err.detail, "attempts_remaining") ?? 0;
                    setError(remaining > 0 ? `Wrong code. ${remaining} ${remaining === 1 ? "try" : "tries"} left.` : "Wrong code. Request a new one.");
                } else if (err.code === "expired" || err.code === "expired_or_invalid") setError("Sign-in session expired. Please try signing in again.");
                else if (err.code === "too_many_attempts") setError("Too many attempts. Request a new code.");
                else if (err.code === "username_taken") setError("That username is already taken. Try another.");
                else if (err.code === "display_name_required" || err.code === "invalid_name") setError("Please enter a valid name (up to 64 characters).");
                else if (err.code === "first_and_last_name_required") setError("Please enter your first and last name.");
                else if (err.code === "terms_required") setError("Please accept the Terms and Privacy Policy.");
                else if (err.code === "hidden_handle_unavailable") setError("Account setup is temporarily unavailable. Please try again.");
                else if (err.code === "date_of_birth_required") setError("Please enter your date of birth.");
                else if (err.code === "age_required" || err.code === "invalid_age") setError("Please enter your age in years.");
                else if (err.code === "invalid_date_of_birth") setError("That date of birth doesn't look right. Please check it.");
                else if (err.code === "invalid_username") setError("Usernames need letters, numbers, dots, dashes, or underscores.");
                else if (err.code === "email_not_allowed") setError("This email address can't be used. Try a different one.");
                else if (err.code === "signup_challenge_required") setError("Extra verification is required for this signup. Please contact support.");
                else if (err.code === "link_failed") setError("Your email was verified, but Instagram could not be linked. Try connecting it from settings.");
                else setError("Something went wrong. Try again.");
            } else {
                setError("Network error. Please try again.");
            }
        } finally {
            setSubmitting(false);
        }
    }

    function submit(e: React.FormEvent) {
        e.preventDefault();
        doSubmit(code, true);
    }

    const providerName = pendingOAuth.provider[0]?.toUpperCase() + pendingOAuth.provider.slice(1);
    const heading = phase === "signup" ? "Create your account" : `Finish with ${providerName}`;
    const subtitle = phase === "email"
        ? `${providerName} does not share your email with us. Verify an email address to finish signing in.`
        : phase === "code"
        ? <>We sent a 6-digit code to <strong>{providerEmail}</strong>.</>
        : "Code verified.";

    return (
        <form onSubmit={submit} noValidate>
            <Head title={heading}/>
            <p className="subtitle">{subtitle}</p>
            <div className="stack">
            {phase === "email" && (
                <div className="field">
                    <label className="label" htmlFor="provider-email">Email</label>
                    <input
                        className="input"
                        id="provider-email"
                        type="email"
                        autoComplete="email"
                        placeholder="you@example.com"
                        value={providerEmail}
                        onChange={(e) => {
                            const nextEmail = e.target.value;
                            setProviderEmail(nextEmail);
                            if (activeDeployment().signupProfile !== "display-name") {
                                setDetails(current => ({...current, userName: current.userName || userNameSuggestionFromEmail(nextEmail)}));
                            }
                        }}
                        autoFocus
                        required
                    />
                </div>
            )}
            {phase === "code" && (
                <CodeInput
                    id="provider-email-code"
                    value={code}
                    onChange={setCode}
                    onComplete={(v) => doSubmit(v)}
                    disabled={submitting}
                    autoFocus
                />
            )}
            {phase === "signup" && (
                <SignupDetailsFields
                    details={details}
                    setDetails={setDetails}
                    showNameFields={true}
                    autoFocusUserName
                    autoFocusFirstName={false}
                />
            )}
            {error && <div className="error-msg" role="alert">{error}</div>}
            <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? <span className="spinner"/> : (phase === "email" ? "Send code" : phase === "code" ? "Verify" : "Create account")}
            </button>
            </div>
        </form>
    );
}

function SignupForm({email, suggestedUserName, oauth, loginId, onDone, setError, error}: {
    loginId?: string | null;
    email: string | null;
    /** Server-derived username hint. Falls back to email prefix if absent. */
    suggestedUserName?: string;
    /** Provider-prefilled names are editable; submission uses the OAuth endpoint. */
    oauth: {token: string; firstName: string; lastName: string} | null;
    onDone: (userId: number | null, returnToOverride?: string | null) => void;
    setError: (s: string | null) => void;
    error: string | null;
}) {
    const [details, setDetails] = useState(() => initialSignupDetails(email, suggestedUserName, {
        firstName: oauth?.firstName ?? "",
        lastName: oauth?.lastName ?? "",
    }));
    const [submitting, setSubmitting] = useState(false);


    async function submit(e: React.FormEvent) {
        e.preventDefault();
        const problem = signupDetailsProblem(details);
        if (problem) { setError(problem); return; }
        setSubmitting(true);
        setError(null);
        try {
            if (oauth) {
                const r = await completeOAuthSignup({token: oauth.token, signup: signupDetailsInput(details)});
                saveTokens(r);
                onDone(r.user_id, r.return_to);
                return;
            }
            const r = await completeSignup(signupDetailsInput(details), loginId ?? undefined);
            saveTokens(r);
            onDone(r.user_id, null);
        } catch (err) {
            if (err instanceof ApiError) {
                if (err.code === "user_create_failed") setError("Couldn't create your account. Please contact support.");
                else if (err.code === "username_taken") setError("That username is already taken. Try another.");
                else if (err.code === "display_name_required" || err.code === "invalid_name") setError("Please enter a valid name (up to 64 characters).");
                else if (err.code === "first_and_last_name_required") setError("Please enter your first and last name.");
                else if (err.code === "terms_required") setError("Please accept the Terms and Privacy Policy.");
                else if (err.code === "hidden_handle_unavailable") setError("Account setup is temporarily unavailable. Please try again.");
                else if (err.code === "date_of_birth_required") setError("Please enter your date of birth.");
                else if (err.code === "age_required" || err.code === "invalid_age") setError("Please enter your age in years.");
                else if (err.code === "invalid_date_of_birth") setError("That date of birth doesn't look right. Please check it.");
                else if (err.code === "invalid_username") setError("Usernames need letters, numbers, dots, dashes, or underscores.");
                else if (err.code === "email_not_allowed") setError("This email address can't be used. Try a different one.");
                else if (err.code === "signup_challenge_required") setError("Extra verification is required for this signup. Please contact support.");
                else if (err.code === "expired_or_invalid") setError("Sign-in session expired. Please try signing in again.");
                else setError("Something went wrong. Try again.");
            } else {
                setError("Network error. Please try again.");
            }
        } finally {
            setSubmitting(false);
        }
    }


    return (
        <form onSubmit={submit} noValidate>
            <Head title="Create your account"/>
            <div className="stack">
            <SignupDetailsFields
                details={details}
                setDetails={setDetails}
                showNameFields={true}
                autoFocusUserName={!!oauth}
                autoFocusFirstName={!oauth}
            />
            {error && <div className="error-msg" role="alert">{error}</div>}
            <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? <span className="spinner"/> : "Create account"}
            </button>
            </div>
        </form>
    );
}

// --- Passkey enrollment offer -----------------------------------------------
// Shown after a successful non-passkey login when the device supports a
// platform authenticator and the user hasn't dismissed the offer before.

function PasskeyOffer({userId, onDone}: {userId: number | null; onDone: () => void}) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    async function setup() {
        setBusy(true);
        setErr(null);
        try {
            await enrollPasskey();
            if (userId !== null) dismissPasskeySetup(userId);
            onDone();
        } catch (e: unknown) {
            if (hasErrorName(e, "NotAllowedError")) { setBusy(false); return; } // user cancelled — keep card
            // InvalidStateError = this device's authenticator is already in
            // excludeCredentials (same passkey enrolled twice). Treat as
            // "already done" and move on.
            if (hasErrorName(e, "InvalidStateError")) {
                if (userId !== null) dismissPasskeySetup(userId);
                onDone();
                return;
            }
            setErr("Couldn't set up the passkey. You can try again from settings.");
            setBusy(false);
        }
    }

    function notNow() {
        if (userId !== null) dismissPasskeySetup(userId);
        onDone();
    }

    return (
        <div className="passkey-offer">
            <div className="icon"><PasskeyIcon size={28}/></div>
            <h2>Faster sign-in next time</h2>
            <p className="subtitle">Use this device's fingerprint, face or screen lock instead of a code.</p>
            {err && <div className="error-msg" role="alert">{err}</div>}
            <div className="actions">
                <button type="button" className="btn btn-primary" onClick={setup} disabled={busy}>
                    {busy ? <span className="spinner"/> : "Set up"}
                </button>
                <button type="button" className="btn btn-ghost btn-md" onClick={notNow} disabled={busy}>Not now</button>
            </div>
        </div>
    );
}

// --- helpers ----------------------------------------------------------------

function isInternalReturnTo() {
    return getReturnTo().internal;
}

function isPhonePagePath(): boolean {
    return typeof window !== "undefined" && /\/login\/phone\/?$/.test(window.location.pathname);
}

function isNativeCallbackPath(): boolean {
    return typeof window !== "undefined" && window.location.pathname.endsWith("/login/native-callback");
}

function isAndroid(): boolean {
    return typeof navigator !== "undefined" && /\bAndroid\b/i.test(navigator.userAgent);
}

function getNativePlatform(): string | null {
    const params = new URLSearchParams(window.location.search);
    const platform = params.get("native_platform");
    if (platform === "android" || platform === "ios") {
        try {
            sessionStorage.setItem(NATIVE_PLATFORM_KEY, platform);
        } catch {
            /* sessionStorage may be unavailable in restricted browser modes */
        }
        return platform;
    }

    try {
        const stored = sessionStorage.getItem(NATIVE_PLATFORM_KEY);
        return stored === "android" || stored === "ios" ? stored : null;
    } catch {
        return null;
    }
}

// Parses /login/done?id=...&signup=1#access_token=...&refresh_token=...
// landed via a magic-link redirect. If tokens are present, persists them
// and triggers the same redirect-to-return_to flow as code/signup completion.
// Returns the initial step + any email to seed the SPA's state machine,
// or null if this isn't a magic landing.
function consumeMagicLanding(): {step: Step; email: string; userId?: number | null; loginId?: string | null} | null {
    if (!window.location.pathname.endsWith("/login/done")) return null;
    const params = new URLSearchParams(window.location.search);
    const fragmentParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    if (!params.get("id")) return null;

    // Wipe the fragment + querystring from history so tokens don't linger.
    const cleanUrl = window.location.origin + "/login/";
    window.history.replaceState(null, "", cleanUrl);

    const access = fragmentParams.get("access_token");
    const refresh = fragmentParams.get("refresh_token");
    if (access && refresh) {
        saveTokens({access_token: access, refresh_token: refresh});
        // App's mount-effect calls completeAuth(userId) → either passkey-offer
        // or immediate redirect.
        return {step: "magic-landed", email: "", userId: userIdFromAccessToken(access)};
    }

    if (params.get("signup") === "1") {
        // Signup-required from a magic-link click. Server passes ?email=...
        // through the redirect so the SignupForm can pre-fill signup details
        // hint from the email prefix.
        return {step: "signup", email: params.get("email") ?? "", loginId: params.get("id")};
    }

    return null;
}

async function redirectToReturnTo(returnToOverride?: string | null): Promise<void> {
    const target = getReturnTo(returnToOverride);
    // A target on this (the auth) origin, the internal /oauth2/* return_to above
    // all, and every target of a relying-party deployment (Forge's PKCE login
    // entry): the rail already set the session cookies, so just navigate and
    // /oauth2/authorize continues with consent + code issuance. None gets the
    // token pair (shared/login-completion.ts): a fragment on an auth-origin URL
    // would ride authorize's redirect into the OAuth client's redirect_uri. (The
    // SPA knows only its own origin; a crafted target on a legacy auth host is
    // refused by the server's rule and cleared by authorize's explicit fragment.)
    if (!loginCompletionMayCarryTokens(target.url, window.location.origin, activeDeployment().returnTo.completion)) {
        window.location.assign(target.url);
        return;
    }
    // External: pass tokens via URL fragment so they aren't logged.
    const t = readTokens();
    if (!t) {
        window.location.assign(target.url);
        return;
    }
    if (isNativeAppLoginReturnTo(target.url)) {
        try {
            const handoff = await createNativeHandoff({
                returnTo: target.url,
                accessToken: t.accessToken,
                refreshToken: t.refreshToken,
            });
            window.location.assign(handoff.redirect_to);
        } catch {
            const fallbackParams = new URLSearchParams({
                access_token: t.accessToken,
                refresh_token: t.refreshToken,
            });
            window.location.assign(`${target.url}#${fallbackParams.toString()}`);
        }
        return;
    }
    const params = new URLSearchParams({
        access_token: t.accessToken,
        refresh_token: t.refreshToken,
    });
    window.location.assign(`${target.url}#${params.toString()}`);
}
